/**
 * Voucher form — where staff mint a code. The production port of the confirmed
 * proof in the design studio, since retired, whose layout was driven and
 * measured in a browser; that proof's claim on this tag ended here, since the
 * two cannot both register it. Mounted in the sheet measure
 * `show()` gives it, footer OUTSIDE the scroll region, the shape
 * `ticket-type-form.view.ts` already uses, so the two admin forms read as one
 * product.
 *
 * ── Nothing on this form ever opens or closes ───────────────────────────────
 * Both halves of what a code does are always on screen at their full height,
 * and there is no switch beside either. A switch is a control whose whole job
 * is to move everything below it — and it would also be a lie: the schema
 * makes `discount` and `unlocks` each nullish VALUES, not modes, so an empty
 * amount IS "no discount" and no type chipped IS "reveals nothing", with
 * nothing left for a flag to disagree with. One line under the heading says
 * what the pair still owes, and the disabled action refuses; neither appears
 * nor disappears.
 *
 * Every message that varies with state renders as one grid cell holding every
 * arm with all but the active one `invisible`, so the cell's height is fixed
 * at first paint and swapping words moves nothing beneath it. A `hint=` on a
 * field cannot do that — the attribute takes one string, and a shorter one is
 * a shorter box — so the fields whose message genuinely changes carry it as a
 * cell below instead.
 *
 * ── 100 % ───────────────────────────────────────────────────────────────────
 * The percentage field accepts 100 — capping the input at 99 refuses the
 * operator without telling them that what they wanted exists under another
 * name. But the teaching cannot live under the field: a block that appears
 * there pushes half the form down at the exact moment the operator is reading
 * a number. So it opens as its own sheet on the CROSSING into 100. That costs
 * nothing in layout, cannot be scrolled past, and carries the move with it —
 * one press switches the code to revealing a €0 ticket type, which is what
 * "free" actually is on the invoice (§ 10 UStG: a Rabatt reduces the Entgelt
 * of a supply; a €0 supply is a different supply).
 *
 * ── The expiry is an instant, in the venue's clock ──────────────────────────
 * `constraints.validUntil` is a `Timestamp` and the document records no
 * authoring zone, so the typed wall clock resolves through
 * `TicketType.resolveEffectiveTimezone` against the event's own venue — the
 * only clock the operator setting an expiry is reading — and the line beneath
 * states the resolved instant and that zone. A bare date would leave a buyer
 * refused on the last day by the one refusal that is nobody's mistake and
 * that the storefront may never explain.
 *
 * ── No event picker ─────────────────────────────────────────────────────────
 * The document lives at `workspaces/{orgId}/events/{eventId}/vouchers/{code}`,
 * so the path already answers which event this code belongs to and no control
 * here may ask again.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { $notify } from "@mhmo91/schmancy/notification";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import { Location, TicketType } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, firstValueFrom, merge, Subject, takeUntil, tap } from "rxjs";
import { session } from "../auth/session.state.ts";
// Registers <accepted-types-calendar>. Its own statement, because the type
// import below is erased and would leave the tag un-upgraded anywhere this
// form loads without the package barrel.
import "../checkin/settings/accepted-types-calendar.view.ts";
import type { AcceptedTypeRow } from "../checkin/settings/accepted-types-calendar.view.ts";
import { eventsState } from "../events/events.state.ts";
import {
	chainClosure,
	ticketTypesState,
} from "../events/ticket-types/ticket-types.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import { locationsState } from "../storefront/locations.state.ts";
import {
	discountLands,
	EMPTY_DRAFT,
	formBlocks,
	generateCode,
	type RedemptionRow,
	redemptionsRequest$,
	redemptionsResult$,
	type VoucherDraft,
	type VoucherWrite,
	vouchersAction$,
	vouchersResult$,
	vouchersState,
} from "./vouchers.state.ts";

@schmancyElement("voucher-form")
export class VoucherForm extends LocalizeMixin(SchmancyElement) {
	/** The event that owns the code — from the console this form is opened
	 *  inside, never from a control on it. */
	@property({ type: String }) eventId = "";
	/** The doc id of the code being edited; empty mints a new one. A code is
	 *  created, never renamed. */
	@property({ type: String }) code = "";

	@stateDecorator() private draft: VoucherDraft = EMPTY_DRAFT;
	@stateDecorator() private usageLimitTouched = false;
	@stateDecorator() private codeWasGenerated = false;
	@stateDecorator() private saving = false;
	@stateDecorator() private activeAction: "save" | "delete" | null = null;
	@stateDecorator() private failedAction: "save" | "delete" | null = null;
	/** Null until the join answers — a code's redemption documents carry no
	 *  email, so each row's name is one order read away. */
	@stateDecorator() private redemptions: readonly RedemptionRow[] | null = null;

	/** The teaching sheet fires on the CROSSING into 100, never on every render
	 *  that happens to read 100 — otherwise correcting the field reopens the
	 *  sheet that told you to correct it. */
	private wasHundred = false;
	private readonly hundredDismissed$ = new Subject<void>();

	override connectedCallback(): void {
		super.connectedCallback();
		const stored = vouchersState.value.find((v) => v.code === this.code);
		if (stored) {
			this.draft = {
				code: stored.code,
				label: stored.label,
				discountType: stored.discount?.type ?? "percent",
				percent:
					stored.discount?.type === "percent" ? stored.discount.value : 0,
				fixedCents:
					stored.discount?.type === "fixed" ? stored.discount.value : 0,
				typeIds: stored.constraints.typeIds ?? [],
				unlocks: stored.unlocks ?? [],
				usageLimit: stored.usageLimit,
				perBuyerLimit: stored.perBuyerLimit ?? 0,
				validUntil: stored.constraints.validUntil
					? Location.instantToWallClock(
							stored.constraints.validUntil.toDate(),
							this.venueTimezone(),
						)
					: "",
			};
			this.wasHundred =
				stored.discount?.type === "percent" && stored.discount.value >= 100;
		}

		if (!this.code) return;
		const requestId = crypto.randomUUID();
		redemptionsResult$
			.pipe(
				filter((r) => r.requestId === requestId),
				tap((r) => {
					this.redemptions = r.rows;
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
		redemptionsRequest$.next({
			requestId,
			eventId: this.eventId,
			code: this.code,
		});
	}

	/** The clock this code's expiry is authored in — the event's own venue,
	 *  resolved the way every other wall-clock field on this event resolves it.
	 *  A voucher carries no venue override of its own, so the type half of that
	 *  resolution is empty by construction. */
	private venueTimezone(): string {
		return TicketType.resolveEffectiveTimezone(
			{ locationId: null },
			{
				locationId:
					eventsState.value.find((e) => e.id === this.eventId)?.locationId ??
					"",
			},
			Object.fromEntries(locationsState.value.map((l) => [l.id, l] as const)),
		);
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	override updated(changed: Map<PropertyKey, unknown>): void {
		super.updated(changed);
		const hundred =
			this.draft.discountType === "percent" && this.draft.percent >= 100;
		if (hundred === this.wasHundred) return;
		this.wasHundred = hundred;
		if (!hundred) return;
		show(
			() => html`
				<div class="flex w-md max-w-full flex-col gap-3 p-5">
					<schmancy-typography type="headline" token="sm">
						${this.t("admin.vouchers.form.hundredHeadline")}
					</schmancy-typography>
					<schmancy-typography type="body" token="md" class="text-surface-onVariant">
						${this.t("admin.vouchers.form.hundredBody")}
					</schmancy-typography>
					<div class="flex flex-wrap gap-2 pt-2">
						<schmancy-button
							variant="filled"
							@click=${() => {
								this.draft = { ...this.draft, percent: 0 };
								this.hundredDismissed$.next();
							}}
						>
							<schmancy-icon slot="prefix" aria-hidden="true">lock_open</schmancy-icon>
							${this.t("admin.vouchers.form.hundredAction")}
						</schmancy-button>
						<schmancy-button
							variant="outlined"
							@click=${() => this.hundredDismissed$.next()}
						>
							${this.t("admin.vouchers.form.hundredDismiss")}
						</schmancy-button>
					</div>
				</div>
			`,
		)
			// The sheet's own two controls end it — the documented programmatic
			// dismiss. A `close` dispatched from this element would end THIS
			// form's overlay instead, which is the one it is nested inside.
			.pipe(takeUntil(merge(this.disconnecting, this.hundredDismissed$)))
			.subscribe();
	}

	private async save(): Promise<void> {
		if (this.saving) return;
		const uid = session.value.uid;
		if (!this.eventId || uid === null) return;
		const tz = this.venueTimezone();
		let validUntil: Date | null = null;
		if (this.draft.validUntil !== "") {
			const instant = Location.wallClockToInstant(this.draft.validUntil, tz);
			if ("error" in instant) {
				$notify.error(
					this.t("admin.vouchers.form.expiryImpossible", {
						when: this.draft.validUntil,
						zone: tz,
					}),
				);
				return;
			}
			validUntil = instant;
		}
		const body: VoucherWrite = {
			label: this.draft.label.trim(),
			discount: !discountLands(this.draft)
				? null
				: this.draft.discountType === "percent"
					? { type: "percent", value: this.draft.percent }
					: { type: "fixed", value: this.draft.fixedCents },
			unlocks: this.draft.unlocks.length > 0 ? [...this.draft.unlocks] : null,
			constraints: {
				typeIds: this.draft.typeIds.length > 0 ? [...this.draft.typeIds] : null,
				validUntil,
			},
			usageLimit: this.draft.usageLimit,
			perBuyerLimit:
				this.draft.perBuyerLimit > 0 ? this.draft.perBuyerLimit : null,
		};

		this.saving = true;
		this.activeAction = "save";
		try {
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				vouchersResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			vouchersAction$.next(
				this.code
					? {
							kind: "update",
							requestId,
							eventId: this.eventId,
							code: this.code,
							body,
						}
					: {
							kind: "create",
							requestId,
							eventId: this.eventId,
							code: this.draft.code,
							createdBy: uid,
							body,
						},
			);
			const result = await resultPromise;
			if (!result.ok) {
				this.failedAction = "save";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "save";
		} finally {
			this.saving = false;
		}
	}

	private async deleteCode(): Promise<void> {
		if (!this.code || !this.eventId || this.saving) return;
		this.saving = true;
		this.activeAction = "delete";
		try {
			const confirmed = await confirm({
				title: this.t("admin.vouchers.form.deleteConfirmTitle"),
				message: this.t("admin.vouchers.form.deleteConfirmMessage"),
				confirmText: this.t("admin.common.delete"),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!confirmed) {
				this.saving = false;
				return;
			}
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				vouchersResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			vouchersAction$.next({
				kind: "delete",
				requestId,
				eventId: this.eventId,
				code: this.code,
			});
			const result = await resultPromise;
			if (!result.ok) {
				this.failedAction = "delete";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "delete";
		} finally {
			this.saving = false;
		}
	}

	override render(): TemplateResult {
		const d = this.draft;
		const blocks = formBlocks(d);
		const editing = this.code !== "";
		const stored = vouchersState.value.find((v) => v.code === this.code);
		const tz = this.venueTimezone();
		const event = eventsState.value.find((e) => e.id === this.eventId) ?? null;
		// The band's own row shape: an id, a name, and the type's EFFECTIVE entry
		// window in the venue's clock, which is what it derives every day column
		// from. Resolved here rather than stored, so a later event.schedule edit
		// keeps reaching a type that inherits its window.
		// Every type a discount can REACH, which is not the same set as the shop:
		// a code-gated type is in a basket the moment another code reveals it,
		// and scoping the money half to the very type the unlock half reveals is
		// the two-beat code the resolver is built for. Listing only
		// buyer-claimable rows left such a scope on screen as no scope at all —
		// the switch reads narrowed, no row reads chosen, and the one type it is
		// narrowed TO is nowhere in the band. Staff types are the exclusion: they
		// resolve that way only at €0 and never enter a buyer's basket, so a
		// discount reaches nothing through them.
		const scopeRows: readonly AcceptedTypeRow[] =
			event === null
				? []
				: ticketTypesState.value
						.filter((t) => TicketType.resolveClaimableBy(t) !== "staff")
						.map((t) => ({
							id: t.id,
							name: t.name,
							entryWindow: {
								opensAt: TicketType.resolveEntryWindow(
									t,
									event,
								).opensAt.toMillis(),
								closesAt: TicketType.resolveEntryWindow(
									t,
									event,
								).closesAt.toMillis(),
							},
							timezone: tz,
						}));
		const hiddenRows: readonly AcceptedTypeRow[] =
			event === null
				? []
				: ticketTypesState.value
						.filter((t) => TicketType.resolveClaimableBy(t) === "code")
						.map((t) => ({
							id: t.id,
							name: t.name,
							entryWindow: {
								opensAt: TicketType.resolveEntryWindow(
									t,
									event,
								).opensAt.toMillis(),
								closesAt: TicketType.resolveEntryWindow(
									t,
									event,
								).closesAt.toMillis(),
							},
							timezone: tz,
						}));
		const scopeChainable = chainClosure(
			d.typeIds,
			ticketTypesState.value,
		).filter((id) => !d.typeIds.includes(id));
		const unlockChainable = chainClosure(
			d.unlocks,
			ticketTypesState.value,
		).filter((id) => !d.unlocks.includes(id));
		const rows = this.redemptions;

		return html`
			<!-- A definite measure, not a minimum: this event's scope and unlock rows
			     carry one chip per ticket type, and a shrink-to-fit sheet would take
			     its width from whichever row is longest — a form that is 34rem wide
			     on one event and full-bleed on the next. Bounded here, each row
			     scrolls inside its own scroller instead, which is what schmancy-chips
			     is for. -->
			<div class="flex max-h-[85vh] w-2xl max-w-full flex-col">
				<div ?inert=${this.saving} ${overflowWithin()} class="flex flex-col gap-4 p-4">
					<schmancy-input
						label=${this.t("admin.vouchers.form.codeLabel")}
						?error=${blocks.includes("code") && d.code !== ""}
						?disabled=${editing}
						.value=${d.code}
						@input=${(e: CustomEvent<{ value: string }>) => {
							const next = e.detail.value.toUpperCase();
							if (next === d.code) return;
							this.draft = { ...d, code: next };
							this.codeWasGenerated = false;
						}}
					>
						<!-- Inside the field's own trailing band, because it acts on this
						     field's value. Kept in the slot even while editing, where it
						     disables rather than vanishing: an action that disappears
						     re-insets the value beside it. -->
						<schmancy-button
							slot="actions"
							variant="text"
							size="sm"
							?disabled=${editing}
							@click=${() => {
								this.draft = {
									...d,
									code: generateCode(),
									// A generated code is by definition for ONE person, so it
									// sets the limits that implies — visibly, on fields the
									// operator can still change, and never over a number they
									// chose themselves.
									...(this.usageLimitTouched
										? {}
										: { usageLimit: 1, perBuyerLimit: 1 }),
								};
								this.codeWasGenerated = true;
							}}
						>
							<schmancy-icon slot="prefix" aria-hidden="true">casino</schmancy-icon>
							${this.t("admin.vouchers.form.generate")}
						</schmancy-button>
					</schmancy-input>
					<!-- Three messages, one cell: the invalid-shape line is one line
					     shorter than the other two, and carried in the field's own
					     hint attribute it would shorten the box and lift the whole form while
					     the operator is still typing the code. -->
					<div class="grid">
						${repeat(
							[
								{
									key: "invalid",
									active: blocks.includes("code") && d.code !== "",
									text: this.t("admin.vouchers.form.codeInvalid"),
									error: true,
								},
								{
									key: "generated",
									active:
										this.codeWasGenerated &&
										!(blocks.includes("code") && d.code !== ""),
									text: this.t("admin.vouchers.form.generatedNote"),
									error: false,
								},
								{
									key: "hint",
									active:
										!this.codeWasGenerated &&
										!(blocks.includes("code") && d.code !== ""),
									text: this.t("admin.vouchers.form.codeHint"),
									error: false,
								},
							],
							(arm) => arm.key,
							(arm) => html`
								<schmancy-typography
									type="body"
									token="sm"
									class=${this.classMap({
										"col-start-1 row-start-1": true,
										"text-surface-onVariant": !arm.error,
										"text-error-default": arm.error,
										invisible: !arm.active,
									})}
									aria-hidden=${arm.active ? "false" : "true"}
								>
									${arm.text}
								</schmancy-typography>
							`,
						)}
					</div>

					<schmancy-input
						label=${this.t("admin.vouchers.form.labelLabel")}
						required
						hint=${this.t("admin.vouchers.form.labelHint")}
						.value=${d.label}
						@input=${(e: CustomEvent<{ value: string }>) => {
							if (e.detail.value === d.label) return;
							this.draft = { ...d, label: e.detail.value };
						}}
					></schmancy-input>

					<schmancy-divider></schmancy-divider>

					<div class="flex flex-col gap-1">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("admin.vouchers.form.jobHeading")}
						</schmancy-typography>
						<!-- Guidance, not a complaint, and permanently in the column: on
						     a blank form nobody has done anything wrong yet, and a line
						     that only appears once something is missing is a line that
						     moves the form when it arrives. -->
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"text-surface-onVariant": true,
								invisible: !blocks.includes("job"),
							})}
							aria-hidden=${blocks.includes("job") ? "false" : "true"}
						>
							${this.t("admin.vouchers.form.jobNone")}
						</schmancy-typography>
					</div>

					<schmancy-surface type="containerLow" class="flex flex-col gap-3 rounded-xl p-3">
						<schmancy-typography type="title" token="sm">
							${this.t("admin.vouchers.form.discountTitle")}
						</schmancy-typography>
						<schmancy-chips
							class="w-full"
							required
							.value=${d.discountType}
							@change=${(e: CustomEvent<{ value: string }>) => {
								if (e.detail.value === d.discountType) return;
								this.draft = {
									...d,
									discountType:
										e.detail.value === "fixed" ? "fixed" : "percent",
								};
							}}
						>
							<schmancy-filter-chip .value=${"percent"}>
								${this.t("admin.vouchers.form.percent")}
							</schmancy-filter-chip>
							<schmancy-filter-chip .value=${"fixed"}>
								${this.t("admin.vouchers.form.fixed")}
							</schmancy-filter-chip>
						</schmancy-chips>

						<!-- One field in one box across both units: swapping the label and
						     the parse keeps the row where it is, where two conditionally
						     rendered fields would trade places under the cursor. -->
						<schmancy-input
							type="number"
							min="0"
							max=${d.discountType === "percent" ? "100" : "999999"}
							label=${
								d.discountType === "percent"
									? this.t("admin.vouchers.form.percentLabel")
									: this.t("admin.vouchers.form.fixedLabel")
							}
							hint=${this.t("admin.vouchers.form.amountEmptyHint")}
							?error=${blocks.includes("hundredPercent")}
							.value=${
								d.discountType === "percent"
									? d.percent === 0
										? ""
										: String(d.percent)
									: d.fixedCents === 0
										? ""
										: String(d.fixedCents / 100)
							}
							@input=${(e: CustomEvent<{ value: string }>) => {
								const raw = e.detail.value.trim();
								if (d.discountType === "percent") {
									const next = raw === "" ? 0 : Number(raw);
									if (next === d.percent) return;
									this.draft = { ...d, percent: next };
									return;
								}
								const next = raw === "" ? 0 : Math.round(Number(raw) * 100);
								if (next === d.fixedCents) return;
								this.draft = { ...d, fixedCents: next };
							}}
						></schmancy-input>

						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.vouchers.form.typeScope")}
						</schmancy-typography>
<accepted-types-calendar
							.types=${scopeRows}
							.event=${event}
							.selected=${d.typeIds}
							@acceptedTypes=${(
								e: CustomEvent<{ value: readonly string[] }>,
							) => {
								this.draft = { ...d, typeIds: [...e.detail.value] };
							}}
						></accepted-types-calendar>
						<!-- Always in the column, invisible when nothing chains — the
						     same permanent-row shape the job line above uses, because a
						     row that arrives when a chip is pressed moves everything
						     under it. A scope naming one link of a showAfterSoldOut
						     chain stops applying the moment that link sells out and its
						     successor takes its place, and settle resolves no chains.
						     Pressing the offer adds them to the selection here rather
						     than expanding silently at save, so what the form shows is
						     what it writes. -->
						<div
							class=${this.classMap({
								"flex flex-wrap items-center gap-2": true,
								invisible: scopeChainable.length === 0,
							})}
							aria-hidden=${scopeChainable.length === 0 ? "true" : "false"}
						>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("admin.vouchers.form.chainScope")}
							</schmancy-typography>
							<schmancy-suggestion-chip
								@action=${() => {
									this.draft = {
										...d,
										typeIds: [...d.typeIds, ...scopeChainable],
									};
								}}
							>
								${this.t("admin.vouchers.form.chainAdd")}
							</schmancy-suggestion-chip>
						</div>
					</schmancy-surface>

					<schmancy-surface type="containerLow" class="flex flex-col gap-3 rounded-xl p-3">
						<schmancy-typography type="title" token="sm">
							${this.t("admin.vouchers.form.unlockTitle")}
						</schmancy-typography>
						${
							hiddenRows.length === 0
								? html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t("admin.vouchers.form.unlockEmpty")}
								</schmancy-typography>
							`
								: html`
<accepted-types-calendar
									emptyMeans="none"
									.types=${hiddenRows}
									.event=${event}
									.selected=${d.unlocks}
									@acceptedTypes=${(
										e: CustomEvent<{ value: readonly string[] }>,
									) => {
										this.draft = { ...d, unlocks: [...e.detail.value] };
									}}
								></accepted-types-calendar>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t("admin.vouchers.form.unlockHint")}
								</schmancy-typography>
								<div
									class=${this.classMap({
										"flex flex-wrap items-center gap-2": true,
										invisible: unlockChainable.length === 0,
									})}
									aria-hidden=${unlockChainable.length === 0 ? "true" : "false"}
								>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${this.t("admin.vouchers.form.chainScope")}
									</schmancy-typography>
									<schmancy-suggestion-chip
										@action=${() => {
											this.draft = {
												...d,
												unlocks: [...d.unlocks, ...unlockChainable],
											};
										}}
									>
										${this.t("admin.vouchers.form.chainAdd")}
									</schmancy-suggestion-chip>
								</div>
							`
						}
					</schmancy-surface>

					<schmancy-divider></schmancy-divider>

					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.vouchers.form.validityHeading")}
					</schmancy-typography>
					<div class="flex flex-col gap-3">
						<schmancy-input
							type="number"
							min="1"
							label=${this.t("admin.vouchers.form.usageLimit")}
							hint=${
								stored
									? this.t("admin.vouchers.form.usedOf", {
											used: stored.usedCount,
											limit: d.usageLimit,
										})
									: ""
							}
							.value=${String(d.usageLimit)}
							@input=${(e: CustomEvent<{ value: string }>) => {
								const next = Number(e.detail.value);
								if (next === d.usageLimit) return;
								this.usageLimitTouched = true;
								this.draft = { ...d, usageLimit: next };
							}}
						></schmancy-input>
						<schmancy-input
							type="number"
							min="0"
							label=${this.t("admin.vouchers.form.perBuyerLimit")}
							hint=${this.t("admin.vouchers.form.perBuyerHint")}
							.value=${d.perBuyerLimit === 0 ? "" : String(d.perBuyerLimit)}
							@input=${(e: CustomEvent<{ value: string }>) => {
								const raw = e.detail.value.trim();
								const next = raw === "" ? 0 : Number(raw);
								if (next === d.perBuyerLimit) return;
								this.draft = { ...d, perBuyerLimit: next };
							}}
						></schmancy-input>
					</div>

					<schmancy-date-field
						type="datetime-local"
						label=${this.t("admin.vouchers.form.validUntil")}
						.value=${d.validUntil}
						@change=${(e: CustomEvent<{ value: string }>) => {
							if (e.detail.value === d.validUntil) return;
							// A day picked with no time means the END of that day when a
							// person says it, and the line below says so out loud rather
							// than leaving the operator to find out from a refusal.
							this.draft = {
								...d,
								validUntil:
									e.detail.value !== "" && !e.detail.value.includes("T")
										? `${e.detail.value}T23:59`
										: e.detail.value,
							};
						}}
					></schmancy-date-field>
					<div class="grid">
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"col-start-1 row-start-1 text-surface-onVariant": true,
								invisible: d.validUntil !== "",
							})}
							aria-hidden=${d.validUntil === "" ? "false" : "true"}
						>
							${this.t("admin.vouchers.form.noExpiry")}
						</schmancy-typography>
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"col-start-1 row-start-1 text-surface-onVariant": true,
								invisible: d.validUntil === "",
							})}
							aria-hidden=${d.validUntil === "" ? "true" : "false"}
						>
							${this.t("admin.vouchers.form.expiresAt", {
								when:
									d.validUntil === ""
										? ""
										: new Date(`${d.validUntil}:00Z`).toLocaleString(
												this.locale,
												{
													dateStyle: "long",
													timeStyle: "short",
													timeZone: "UTC",
												},
											),
								zone: tz,
							})}
						</schmancy-typography>
					</div>

					${when(
						editing,
						() => html`
							<schmancy-divider></schmancy-divider>
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
								${this.t("admin.vouchers.form.redemptions")}
							</schmancy-typography>
							${
								rows === null
									? html`
									<div role="status" aria-busy="true" class="flex flex-col gap-2">
										<schmancy-visually-hidden>
											${this.t("admin.vouchers.form.redemptionsLoading")}
										</schmancy-visually-hidden>
										<schmancy-skeleton shape="text" aria-hidden="true" class="w-2/3"></schmancy-skeleton>
									</div>
								`
									: rows.length === 0
										? html`
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${this.t("admin.vouchers.form.redemptionsNone")}
										</schmancy-typography>
									`
										: html`
										<div class="flex flex-col gap-2">
											${repeat(
												rows,
												(r) => r.buyerKey,
												(r) => html`
													<div class="flex items-baseline justify-between gap-3">
														<div class="flex min-w-0 flex-col">
															<schmancy-typography type="body" token="md">
																${
																	r.name ??
																	this.t(
																		"admin.vouchers.form.redemptionUnknownBuyer",
																		{
																			orderId: r.lastOrderId,
																		},
																	)
																}
															</schmancy-typography>
															<schmancy-typography type="body" token="sm" class="truncate text-surface-onVariant">
																${r.email ?? ""}
															</schmancy-typography>
														</div>
														<schmancy-typography type="body" token="sm" class="tabular-nums text-surface-onVariant">
															${
																r.count > 1
																	? html`${this.t("admin.vouchers.form.redemptionCount", { count: r.count })} · `
																	: ""
															}${new Date(r.lastRedeemedAtMs).toLocaleDateString(this.locale)}
														</schmancy-typography>
													</div>
												`,
											)}
										</div>
									`
							}
						`,
					)}
				</div>

				<!-- Outside the scroll region (location-form.view.ts's pattern), so it
				     never overlays content nor strands dead space under itself. -->
				<schmancy-surface
					opacity="translucent"
					class="flex flex-wrap items-center gap-3 px-4 pt-2 pb-4"
				>
					${when(
						editing,
						() => html`
							<schmancy-button
								variant="outlined"
								color="error"
								?disabled=${this.saving && this.activeAction !== "delete"}
								.busy=${this.saving && this.activeAction === "delete"} @click=${() => this.deleteCode()}
							>
								<span style="display:grid"><span style=${this.failedAction === "delete" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "delete" ? "true" : "false"}>${this.t("admin.common.delete")}</span><span style=${this.failedAction === "delete" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "delete" ? "false" : "true"}>${this.t("admin.common.retryDelete")}</span></span>
							</schmancy-button>
						`,
					)}
					<div class="ml-auto flex flex-wrap justify-end gap-2">
						<schmancy-button variant="outlined" ?disabled=${this.saving} @click=${() => this.close()}>
							${this.t("admin.vouchers.form.cancel")}
						</schmancy-button>
						<schmancy-button
							variant="filled"
							?disabled=${blocks.length > 0 || (this.saving && this.activeAction !== "save")}
							 .busy=${this.saving && this.activeAction === "save"} color=${this.failedAction === "save" ? "error" : "primary"} @click=${() => this.save()}
						>
							<span style="display:grid"><span style=${this.failedAction === "save" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "save" ? "true" : "false"}>${this.t(editing ? "admin.vouchers.form.saveExisting" : "admin.vouchers.form.save")}</span><span style=${this.failedAction === "save" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "save" ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
						</schmancy-button>
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"voucher-form": VoucherForm;
	}
}
